// src/components/Admin/ClientEditor.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  ExternalLink,
  Loader2,
  AlertCircle,
  Check,
  Trash2,
} from "lucide-react";
import {
  getClient,
  createClient,
  updateClient,
  deleteClient,
  validateSlug,
  generateSlug,
} from "../../services/adminService";
import OverviewTab from "./tabs/OverviewTab";
import AvatarsTab from "./tabs/AvatarsTab";
import FilesTab from "./tabs/FilesTab";
import ConfirmDialog from "./shared/ConfirmDialog";

// ============================================
// DEFAULT CONFIG for new clients
// ============================================

const getDefaultConfig = () => ({
  version: "2.0.0",
  clientSlug: "",
  client: {
    name: "",
    projectName: "",
    contactEmail: "",
    logoUrl: null,
    primaryColor: "#030712",
    accentColor: "#15803d",
  },
  states: [],
  avatars: [],
  maps: [],
  globalReports: [
    {
      id: "faq",
      title: "Frequently Asked Questions",
      fileName: "faq.md",
      storagePath: "reports/faq.md",
      type: "markdown",
      icon: "help-circle",
    },
  ],
  features: {
    competitors: { enabled: false },
    proxyBusinesses: { enabled: false },
    search: {
      enabled: true,
      searchableFields: ["city", "zcta", "geoId", "county"],
    },
    download: { enabled: true, formats: ["xlsx", "csv"] },
    reports: { enabled: true },
  },
});

// ============================================
// TABS
// ============================================

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "avatars", label: "Avatars & Maps" },
  { id: "files", label: "Files" },
];

// ============================================
// VALIDATION
// ============================================

const validateConfig = (config, slug, isNew) => {
  const errors = [];

  // Slug
  if (isNew) {
    const slugError = validateSlug(slug);
    if (slugError) errors.push(slugError);
  }

  // Client name
  if (!config.client?.name?.trim()) {
    errors.push("Client name is required");
  }

  // States
  if (config.states?.length === 0) {
    errors.push("At least one state is required");
  }
  config.states?.forEach((s, i) => {
    if (!s.abbreviation)
      errors.push(`State ${i + 1}: abbreviation is required`);
    if (!s.name) errors.push(`State ${i + 1}: name is required`);
  });

  // Avatars
  config.avatars?.forEach((a, i) => {
    if (!a.id) errors.push(`Avatar ${i + 1}: ID is required`);
    if (!a.name) errors.push(`Avatar ${i + 1}: name is required`);
  });

  // Maps
  config.maps?.forEach((m, i) => {
    if (!m.id) errors.push(`Map ${i + 1}: ID is required`);
    if (!m.avatarId) errors.push(`Map ${i + 1}: avatar is required`);
    if (!m.stateAbbreviation) errors.push(`Map ${i + 1}: state is required`);
    if (!m.geographyType)
      errors.push(`Map ${i + 1}: geography type is required`);
  });

  // Check for duplicate IDs
  const avatarIds = config.avatars?.map((a) => a.id).filter(Boolean);
  const dupAvatars = avatarIds?.filter((id, i) => avatarIds.indexOf(id) !== i);
  if (dupAvatars?.length)
    errors.push(`Duplicate avatar IDs: ${dupAvatars.join(", ")}`);

  const mapIds = config.maps?.map((m) => m.id).filter(Boolean);
  const dupMaps = mapIds?.filter((id, i) => mapIds.indexOf(id) !== i);
  if (dupMaps?.length) errors.push(`Duplicate map IDs: ${dupMaps.join(", ")}`);

  return errors;
};

// ============================================
// CLIENT EDITOR COMPONENT
// ============================================

const ClientEditor = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const isNew = !slug;

  // State
  const [config, setConfig] = useState(getDefaultConfig());
  const [status, setStatus] = useState("draft");
  const [clientSlug, setClientSlug] = useState("");
  const [activeTab, setActiveTab] = useState("overview");

  // Loading / saving
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  // Delete dialog
  const [showDelete, setShowDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Track original for dirty detection
  const [originalJson, setOriginalJson] = useState("");
  const currentJson = useMemo(
    () => JSON.stringify({ config, status, clientSlug }),
    [config, status, clientSlug],
  );
  const isDirty = currentJson !== originalJson;

  // ---- Fetch existing client ----
  useEffect(() => {
    if (isNew) {
      setOriginalJson(
        JSON.stringify({
          config: getDefaultConfig(),
          status: "draft",
          clientSlug: "",
        }),
      );
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const client = await getClient(slug);
        if (!client) {
          setLoadError(`Client "${slug}" not found`);
          return;
        }
        const clientConfig = client.config || getDefaultConfig();
        setConfig(clientConfig);
        setStatus(client.status || "draft");
        setClientSlug(slug);
        const snapshot = JSON.stringify({
          config: clientConfig,
          status: client.status || "draft",
          clientSlug: slug,
        });
        setOriginalJson(snapshot);
      } catch (err) {
        setLoadError(err.message || "Failed to load client");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug, isNew]);

  // Clear save success after 3s
  useEffect(() => {
    if (saveSuccess) {
      const t = setTimeout(() => setSaveSuccess(false), 3000);
      return () => clearTimeout(t);
    }
  }, [saveSuccess]);

  // ---- Config updaters ----
  const updateConfig = (patch) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  };

  // ---- Save ----
  const handleSave = async () => {
    setSaveError(null);
    setValidationErrors([]);

    const finalSlug = isNew ? clientSlug : slug;

    // Validate
    const errors = validateConfig(config, finalSlug, isNew);
    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSaving(true);
    try {
      const finalConfig = { ...config, clientSlug: finalSlug };

      if (isNew) {
        await createClient(finalSlug, finalConfig);
      } else {
        await updateClient(finalSlug, { config: finalConfig, status });
      }

      const snapshot = JSON.stringify({
        config: finalConfig,
        status,
        clientSlug: finalSlug,
      });
      setOriginalJson(snapshot);
      setSaveSuccess(true);

      if (isNew) {
        // Navigate to the edit page for the new client
        navigate(`/admin/clients/${finalSlug}`, { replace: true });
      }
    } catch (err) {
      setSaveError(err.message || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete ----
  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteClient(slug, true);
      navigate("/admin", { replace: true });
    } catch (err) {
      setSaveError(err.message || "Failed to delete client");
      setDeleting(false);
    }
  };

  // ---- Auto-suggest slug from name ----
  const handleClientChange = (clientInfo) => {
    updateConfig({ client: clientInfo });
    if (isNew && !clientSlug && clientInfo.name) {
      setClientSlug(generateSlug(clientInfo.name));
    }
  };

  // ---- Render ----

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading client...</span>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        <Link
          to="/admin"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <ArrowLeft size={16} />
          Back to Clients
        </Link>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <AlertCircle size={24} className="mx-auto text-red-400 mb-2" />
          <p className="text-sm text-red-700">{loadError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24">
      {/* Back link + title */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <Link
            to="/admin"
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft size={20} />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              {isNew ? "New Client" : config.client?.name || slug}
            </h1>
            {!isNew && (
              <p className="text-xs text-gray-500 font-mono">/{slug}</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isNew && status === "active" && (
            <a
              href={`/${slug}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-secondary border border-gray-300 rounded-lg transition"
            >
              <ExternalLink size={14} />
              View Site
            </a>
          )}
          {!isNew && (
            <button
              type="button"
              onClick={() => setShowDelete(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition"
            >
              <Trash2 size={14} />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-0 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition whitespace-nowrap ${
                activeTab === tab.id
                  ? "border-secondary text-secondary"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Validation errors */}
      {validationErrors.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle
              size={16}
              className="text-red-500 mt-0.5 flex-shrink-0"
            />
            <div>
              <p className="text-sm font-medium text-red-800 mb-1">
                Please fix the following issues:
              </p>
              <ul className="text-sm text-red-700 space-y-0.5">
                {validationErrors.map((err, i) => (
                  <li key={i}>• {err}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Tab content */}
      {activeTab === "overview" && (
        <OverviewTab
          clientInfo={config.client || {}}
          clientSlug={clientSlug}
          states={config.states || []}
          status={status}
          features={config.features || {}}
          globalReports={config.globalReports || []}
          isNew={isNew}
          onClientChange={handleClientChange}
          onSlugChange={setClientSlug}
          onStatesChange={(states) => updateConfig({ states })}
          onStatusChange={setStatus}
          onFeaturesChange={(features) => updateConfig({ features })}
          onGlobalReportsChange={(globalReports) =>
            updateConfig({ globalReports })
          }
        />
      )}

      {activeTab === "avatars" && (
        <AvatarsTab
          avatars={config.avatars || []}
          maps={config.maps || []}
          states={config.states || []}
          onAvatarsChange={(avatars) => updateConfig({ avatars })}
          onMapsChange={(maps) => updateConfig({ maps })}
        />
      )}

      {activeTab === "files" && (
        <FilesTab clientSlug={isNew ? clientSlug : slug} config={config} />
      )}

      {/* ============ STICKY SAVE BAR ============ */}
      <div className="fixed bottom-0 left-0 right-0 lg:left-56 z-40 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isDirty && (
              <span className="text-xs text-amber-600 font-medium">
                Unsaved changes
              </span>
            )}
            {saveSuccess && (
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-medium">
                <Check size={14} />
                Saved
              </span>
            )}
            {saveError && (
              <span className="text-xs text-red-600">{saveError}</span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/admin"
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition"
            >
              Cancel
            </Link>
            <button
              onClick={handleSave}
              disabled={saving || !isDirty}
              className="inline-flex items-center gap-2 px-5 py-2 bg-secondary text-white text-sm font-medium rounded-lg hover:bg-secondary-dark transition shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Save size={16} />
              )}
              {isNew ? "Create Client" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={showDelete}
        onClose={() => setShowDelete(false)}
        onConfirm={handleDelete}
        loading={deleting}
        title={`Delete "${config.client?.name || slug}"?`}
        message="This will permanently delete the client configuration and all associated files from Firebase. This action cannot be undone."
        confirmLabel="Delete Client"
        confirmStyle="danger"
      />
    </div>
  );
};

export default ClientEditor;
